import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { Appointment } from 'types/appointments';
import { useSubmitApi } from 'hooks/customHooks/authHook';
import { ApiCalls } from '../../../components/forms/providerAppointmentForms/AllAppointmentsApi';
import moment from 'moment';
import CorticaLoader from '../../../components/sharedComponents/CorticaLoader/CorticaLoader';
import Popup from '../../../components/sharedComponents/reactHookComponents/popup/popup';
import { CustomAppointmentWrapper } from '../../../components/forms/providerAppointmentForms/style';
import { useTheme, useMediaQuery, Box, Typography } from '@mui/material';
import { FormItem } from '../../../components/sharedComponents/reactHookComponents/formItem';
import './ViewTeacherSignature.scss';

interface TeacherSignatureData {
  eventId?: number; 
  isUnavailableStatus: boolean;
  teacherFullName: string | null;
  unavailableStatusNotes: string | null;
  s3Key: string | null;
  presignedUrl?: string;
  createdAt?: string | Date;
}

const ViewTeacherSignature: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getApiCall } = useSubmitApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const appointment = location.state?.appointment as Appointment;

  const [signatureData, setSignatureData] = useState<TeacherSignatureData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);

  useEffect(() => {
    if (!appointment) {
      navigate('/providerappointments');
      return;
    }

    fetchSignatureData();
  }, [appointment, navigate]);

  const fetchSignatureData = async () => {
    if (!appointment?.id) {
      setError('Invalid appointment');
      setShowErrorPopup(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsNotFound(false);

    try {
      const response = await getApiCall(
        `${ApiCalls.GetTeacherSignature}/${appointment.id}`
      );
      
      setSignatureData(response.data);
    } catch (err: any) {
      console.error('[ViewTeacherSignature] Error fetching signature:', err);
      if (err?.response?.status === 404) {
        setIsNotFound(true);
        setError(null);
      } else {
        const errorMessage = err?.response?.data?.error?.message || 
                            err?.message || 
                            'Failed to load signature. Please try again.';
        setError(errorMessage);
        setShowErrorPopup(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/providerappointments', {
      state: {
        prefStartDate: appointment?.start,
      },
    });
  };

  const formatAppointmentDetails = () => {
    if (!appointment) return '';
    const startDate = moment(appointment.startAt);
    const endDate = moment(appointment.endAt);
    const formattedDate = startDate.format('dddd, MMMM D, YYYY');
    const formattedStartTime = startDate.format('hh:mm a');
    const formattedEndTime = endDate.format('hh:mm a');
    const clientName = appointment.client_name || 'N/A';
    
    return `Teacher/School Admin sign-off for ${clientName} on ${formattedDate} from ${formattedStartTime} - ${formattedEndTime}`;
  };

  if (isLoading) {
    return <CorticaLoader message="Loading signature..." />;
  }

  return (
    <CustomAppointmentWrapper className="view-teacher-signature-page">
      <Popup
        isOpen={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        heading="Error"
        paragraph={error || 'An error occurred while loading the signature.'}
      />

      <div className="form-enclosure max-span">
        <div className="main-form view-teacher-signature-card">
          
          <div className="w-100 d-flex header-wrapper">
            <div className="header-div">
              <div className="back-to v-center">
                <button onClick={handleBack}>
                  <FontAwesomeIcon className="icon-back" icon={faChevronLeft} />
                </button>
              </div>
              <div className="page-desc v-center">
                <span>View Signature</span>
              </div>
            </div>
          </div>

          <Box className="view-teacher-signature-copy">
            <Typography variant="body1" sx={{ color: '#122f4e', fontSize: isMobile ? '14px' : '16px' }}>
              {formatAppointmentDetails()}
            </Typography>
          </Box>

          {signatureData && Boolean(signatureData.isUnavailableStatus) && (
            <div className="form-row collect-row">
              <FormItem optional={false} label="">
                <label className="checkbox-label checkbox-label-disabled">
                  <input
                    type="checkbox"
                    checked={Boolean(signatureData.isUnavailableStatus)}
                    disabled
                    className="checkbox-input"
                  />
                  <span>Teacher/School Admin Unavailable</span>
                </label>
              </FormItem>
            </div>
          )}

          {isNotFound ? (
            <div className="signature-details">
              <div className="no-signature-state">
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#666', 
                    textAlign: 'center',
                    padding: 3,
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  No teacher signature has been collected for this event yet.
                </Typography>
              </div>
            </div>
          ) : signatureData ? (
            <div className="signature-details">
              {Boolean(signatureData.isUnavailableStatus) ? (
                <>
                  {signatureData.unavailableStatusNotes && (
                    <div className="form-row collect-row">
                      <FormItem optional={true} label="Notes">
                        <textarea
                          value={signatureData.unavailableStatusNotes}
                          readOnly
                          className="notes-textarea"
                          rows={4}
                        />
                      </FormItem>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {signatureData.teacherFullName && (
                    <div className="form-row collect-row">
                      <FormItem optional={false} label="">
                        <input
                          type="text"
                          value={signatureData.teacherFullName}
                          readOnly
                          className="text-input"
                        />
                      </FormItem>
                    </div>
                  )}
                  {signatureData.presignedUrl && (
                    <div className="form-row collect-row">
                      <FormItem optional={false} label="">
                        <div className="signature-container">
                          <img
                            src={signatureData.presignedUrl}
                            alt="Teacher signature"
                            className="signature-image"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const errorDiv = document.createElement('div');
                              errorDiv.className = 'signature-error';
                              errorDiv.textContent = 'Failed to load signature image';
                              target.parentElement?.appendChild(errorDiv);
                            }}
                          />
                        </div>
                      </FormItem>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </CustomAppointmentWrapper>
  );
};

export default ViewTeacherSignature;
